// AB#1818 — Super Admin Categories management page.
// AB#1872 — state collapse: the lifecycle is now a single Active ↔ Deactivated
// axis. Tabs are All · Deactivated only (Pending approval + Inactive removed).
// The approval surface (Approve / Reject / Pending) is gone — SuperAdmin-created
// categories auto-activate. Archive→Deactivate, Restore→Reactivate (founder
// terminology), driven off `archivedAt` (storage column unchanged).
// AB#1870 — each row carries an optional gender pill ("For women"/"For men")
// when `targetGender != NoPreference`; the create/edit modal owns the control.
// AB#1871 — Deactivate opens the impact dialog with lazy accordion drill-down.
//
// State integrity: the page reads the full admin list once (`mode=All`,
// deactivated included) and derives the Deactivated tab CLIENT-SIDE off
// `archivedAt`.
//
// Data flow: page → useAdminCategories (TanStack Query) → apiService → backend.
// Writes go through the useCategoriesAdmin mutations which invalidate the
// `['admin','categories']` cache on success.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../../components/Button/Button';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import {
    useAdminCategories,
    useReactivateCategory,
} from '../../../hooks/useCategoriesAdmin';
import { type Category, CategoryTargetGender } from '../../../types/api-responses';
import { resolveImageUrl } from '../../../utils/imageUrl';
import CreateCategoryModal from './CreateCategoryModal';
import DeactivateCategoryModal from './DeactivateCategoryModal';
import {
    buildCategoryRowActions,
    isCategoryDeactivated as isDeactivated,
    type CategoryRowAction,
} from './categoryRowActions';
import styles from './CategoriesPage.module.css';

type Tab = 'all' | 'deactivated';

interface BadgeProps {
    label: string;
    variant: 'active' | 'deactivated';
}

const Badge: React.FC<BadgeProps> = ({ label, variant }) => {
    const variantClass: Record<BadgeProps['variant'], string> = {
        active: styles.badgeActive,
        deactivated: styles.badgeArchived,
    };
    return <span className={`${styles.badge} ${variantClass[variant]}`}>{label}</span>;
};

/** AB#1870 — gender pill. Bound as an SR unit via the accessible label. */
const GenderPill: React.FC<{ category: Category }> = ({ category }) => {
    const { t } = useLanguage();
    const tg = category.targetGender ?? CategoryTargetGender.NoPreference;
    if (tg === CategoryTargetGender.NoPreference) return null;
    const isFemale = tg === CategoryTargetGender.Female;
    const label = t(isFemale ? 'categories.target.female' : 'categories.target.male');
    return (
        <span
            className={`${styles.genderPill} ${isFemale ? styles.genderPillFemale : styles.genderPillMale}`}
            aria-label={`${category.name} — ${label}`}
        >
            {label}
        </span>
    );
};

/**
 * Lightweight keyboard-accessible overflow ("…") menu. Closes on outside click
 * and Escape. Mirrors cleanly under [dir="rtl"] via logical positioning.
 */
interface OverflowMenuProps {
    label: string;
    items: { key: string; label: string; onClick: () => void }[];
}

const OverflowMenu: React.FC<OverflowMenuProps> = ({ label, items }) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div className={styles.overflow} ref={rootRef}>
            <button
                type="button"
                className={styles.overflowTrigger}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={label}
                onClick={() => setOpen((v) => !v)}
            >
                <span aria-hidden="true">⋯</span>
            </button>
            {open && (
                <div className={styles.overflowMenu} role="menu">
                    {items.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            role="menuitem"
                            className={styles.overflowItem}
                            onClick={() => {
                                setOpen(false);
                                item.onClick();
                            }}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const SkeletonRow: React.FC = () => (
    <div className={styles.skeletonRow} aria-hidden="true">
        <div className={`${styles.skeleton} ${styles.skeletonThumb}`} />
        <div>
            <div className={`${styles.skeleton} ${styles.skeletonLine}`} style={{ width: '60%' }} />
            <div className={`${styles.skeleton} ${styles.skeletonLine} ${styles.skeletonLineShort}`} />
        </div>
        <div className={`${styles.skeleton} ${styles.skeletonLine}`} style={{ width: 80 }} />
    </div>
);

const CategoriesPage: React.FC = () => {
    const { t, language } = useLanguage();
    const { showToast } = useToast();
    const { data, isLoading, isError, refetch, isFetching } = useAdminCategories();
    const reactivateMutation = useReactivateCategory();

    const [activeTab, setActiveTab] = useState<Tab>('all');
    const [createOpen, setCreateOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<Category | null>(null);
    const [deactivateTarget, setDeactivateTarget] = useState<Category | null>(null);

    const categories: Category[] = useMemo(() => data?.categories ?? [], [data]);

    const deactivatedCategories = useMemo(
        () => categories.filter((c) => isDeactivated(c)),
        [categories],
    );

    const visibleCategories: Category[] = useMemo(() => {
        switch (activeTab) {
            case 'deactivated':
                return deactivatedCategories;
            case 'all':
            default:
                return categories;
        }
    }, [activeTab, categories, deactivatedCategories]);

    const renderTabLabel = (key: Tab, count: number) => {
        const label = t(`superadmin.categories.tab.${key}`);
        // Wrap numeric portion in <span dir="ltr"> so bidi keeps parens + digits
        // LTR within an Arabic tab label.
        return (
            <>
                <span>{label}</span>
                <span className={styles.tabCount} dir="ltr">
                    {count}
                </span>
            </>
        );
    };

    const lifecycleBadge = (c: Category) =>
        isDeactivated(c) ? (
            <Badge variant="deactivated" label={t('superadmin.categories.status.deactivated')} />
        ) : (
            <Badge variant="active" label={t('superadmin.categories.status.active')} />
        );

    const onReactivate = async (categoryId: number) => {
        try {
            await reactivateMutation.mutateAsync(categoryId);
            showToast(t('superadmin.categories.toast.reactivated'), 'success');
        } catch {
            showToast(t('superadmin.categories.toast.error'), 'error');
        }
    };

    const renderRow = (c: Category) => {
        const deactivated = isDeactivated(c);
        const thumbSrc = resolveImageUrl(c.imageUrl);

        const actionConfig: Record<
            CategoryRowAction,
            { labelKey: string; onClick: () => void }
        > = {
            edit: {
                labelKey: 'superadmin.categories.action.edit',
                onClick: () => setEditTarget(c),
            },
            deactivate: {
                labelKey: 'superadmin.categories.action.deactivate',
                onClick: () => setDeactivateTarget(c),
            },
        };
        const overflowItems: OverflowMenuProps['items'] = buildCategoryRowActions(c).map(
            (key) => ({
                key,
                label: t(actionConfig[key].labelKey),
                onClick: actionConfig[key].onClick,
            }),
        );

        return (
            <div key={c.id} className={styles.row}>
                {thumbSrc ? (
                    <img
                        src={thumbSrc}
                        alt=""
                        className={styles.thumb}
                        loading="lazy"
                        onError={(e) => {
                            const target = e.currentTarget;
                            target.style.display = 'none';
                            const next = target.nextElementSibling as HTMLElement | null;
                            if (next) next.style.display = 'flex';
                        }}
                    />
                ) : null}
                <div
                    className={styles.thumbFallback}
                    style={{ display: thumbSrc ? 'none' : 'flex' }}
                    aria-hidden="true"
                >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
                        <circle cx="9" cy="11" r="1.5" fill="currentColor" />
                        <path d="M5 17l4-4 3 3 5-6 3 4" stroke="currentColor" strokeWidth="1.5" fill="none" />
                    </svg>
                </div>

                <div className={styles.rowMeta}>
                    <div className={styles.rowName}>{c.name}</div>
                    {c.nameAr && <div className={styles.rowNameAr}>{c.nameAr}</div>}
                    <div className={styles.rowBadges}>
                        {lifecycleBadge(c)}
                        <GenderPill category={c} />
                    </div>
                </div>

                <div className={styles.rowActions}>
                    {deactivated && (
                        <Button
                            label={t('superadmin.categories.action.reactivate')}
                            variant="secondary"
                            onClick={() => onReactivate(c.id)}
                            disabled={reactivateMutation.isPending}
                            size="small"
                        />
                    )}
                    {overflowItems.length > 0 && (
                        <OverflowMenu
                            label={t('superadmin.categories.action.more')}
                            items={overflowItems}
                        />
                    )}
                </div>
            </div>
        );
    };

    const emptyKey =
        activeTab === 'deactivated'
            ? 'superadmin.categories.empty.deactivated'
            : 'superadmin.categories.empty.all';

    return (
        <div className={styles.container} lang={language}>
            <div className={styles.header}>
                <div>
                    <h1 className={styles.title}>{t('superadmin.categories.title')}</h1>
                    <p className={styles.subtitle}>{t('superadmin.categories.subtitle')}</p>
                </div>
                {/* AB#1873 — .headerAction (flex:0 0 auto) sizes the CTA to its
                    content so the shared Button's width:100% can't stretch it into
                    a full-width bar. */}
                <div className={styles.headerAction}>
                    <Button
                        label={t('superadmin.categories.new')}
                        onClick={() => setCreateOpen(true)}
                    />
                </div>
            </div>

            <div className={styles.tabs} role="tablist" aria-label={t('superadmin.categories.title')}>
                <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'all'}
                    className={`${styles.tab} ${activeTab === 'all' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('all')}
                >
                    {renderTabLabel('all', categories.length)}
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'deactivated'}
                    className={`${styles.tab} ${activeTab === 'deactivated' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('deactivated')}
                >
                    {renderTabLabel('deactivated', deactivatedCategories.length)}
                </button>
            </div>

            {isError && (
                <div className={styles.errorBanner} role="alert">
                    <span>{t('superadmin.categories.error.fetch')}</span>
                    <Button
                        label={t('superadmin.categories.retry')}
                        size="small"
                        variant="secondary"
                        onClick={() => refetch()}
                        disabled={isFetching}
                    />
                </div>
            )}

            {isLoading ? (
                <div className={styles.listWrapper}>
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                </div>
            ) : visibleCategories.length === 0 && !isError ? (
                <div className={styles.emptyState}>
                    <h3>{t(emptyKey)}</h3>
                </div>
            ) : visibleCategories.length > 0 ? (
                <div className={styles.listWrapper}>
                    {visibleCategories.map(renderRow)}
                </div>
            ) : null}

            <CreateCategoryModal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
            />
            <CreateCategoryModal
                open={editTarget !== null}
                category={editTarget}
                onClose={() => setEditTarget(null)}
            />
            <DeactivateCategoryModal
                open={deactivateTarget !== null}
                categoryId={deactivateTarget?.id ?? null}
                categoryName={deactivateTarget?.name ?? ''}
                onClose={() => setDeactivateTarget(null)}
            />
        </div>
    );
};

export default CategoriesPage;
